import { Audio, AudioStatusReasonType, AudioStatusType } from 'types/audio';
import { ContextMenuOption } from 'types/general.types';
import { AudioDialogVariant } from 'app/components/Song/Audio/AudioTableList/types';
import format from 'date-fns/format';
import { dateFormats } from 'app/constants';

export const getMenuOptions: (props: {
  audio: Audio;
  downloadAudioHref?: string;
  deleteAudioHref?: string;
  isShareAudioEnabled: boolean;
  setDialogOpen: (props: AudioDialogVariant) => void;
  handleDownload: (audio: Audio) => void;
  handleSetLatestAudioVersion: (audio: Audio) => void;
  handleRemoveLatestAudioVersion: (audio: Audio) => void;
  addToPlaylist?: (audio: Audio) => void;
}) => ContextMenuOption[] = ({
  audio,
  downloadAudioHref,
  deleteAudioHref,
  isShareAudioEnabled,
  setDialogOpen,
  handleDownload,
  handleSetLatestAudioVersion,
  handleRemoveLatestAudioVersion,
  addToPlaylist,
}) => {
  const canDownload =
    audio?.status === AudioStatusType.SUCCESS && downloadAudioHref;
  const canDelete = Boolean(deleteAudioHref);
  const canSetAsLatest =
    !audio.isLatestVersion && audio.status === AudioStatusType.SUCCESS;
  const canRemoveLatest = audio.isLatestVersion;
  const canShare =
    isShareAudioEnabled && audio.statusReason === AudioStatusReasonType.SUCCESS;
  return [
    ...(canShare
      ? [
          {
            title: 'Share',
            value: 'SHARE',
            onClick: () => setDialogOpen({ type: 'share', audio }),
          },
        ]
      : []),
    ...(addToPlaylist
      ? [
          {
            title: 'Add to playlist',
            value: 'PLAYLIST',
            onClick: () => setDialogOpen({ type: 'playlist', audio }),
          },
        ]
      : []),
    {
      title: 'Edit',
      value: 'EDIT',
      onClick: () => setDialogOpen({ type: 'edit', audio }),
    },
    ...(canDownload
      ? [
          {
            title: 'Download',
            value: 'DOWNLOAD',
            onClick: () => handleDownload(audio),
          },
        ]
      : []),
    ...(canSetAsLatest
      ? [
          {
            title: 'Mark as latest version',
            value: 'MARK_AS_LATEST',
            onClick: () => {
              if (audio.isPrivate) {
                setDialogOpen({ type: 'private', audio });
              } else {
                handleSetLatestAudioVersion(audio);
              }
            },
          },
        ]
      : []),
    ...(canRemoveLatest
      ? [
          {
            title: 'Unmark as latest version',
            value: 'UNMARK_LATEST',
            onClick: () => handleRemoveLatestAudioVersion(audio),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            title: 'Delete',
            value: 'DELETE',
            onClick: () => setDialogOpen({ type: 'delete', audio }),
          },
        ]
      : []),
  ];
};

export const getDateColumnText = (audio: Audio) => {
  if (
    [
      AudioStatusReasonType.UPLOADING,
      AudioStatusReasonType.READY_FOR_UPLOAD,
    ].includes(audio.statusReason)
  ) {
    return 'Uploading';
  }
  if (audio.statusReason === AudioStatusReasonType.TRANSCODING) {
    return 'Transcoding';
  }
  return format(audio.created.timestamp * 1000, dateFormats.LIST_FORMAT);
};

export const getErrorText = (audio: Audio) => {
  if (audio.statusReason === AudioStatusReasonType.FAILURE) {
    return 'Unexpected failure occurred.';
  }
  if (audio.statusReason === AudioStatusReasonType.UNSUPPORTED_FILE_TYPE) {
    return 'Unsupported file type.';
  }
  if (audio.statusReason === AudioStatusReasonType.REACHED_MAXIMUM_FILE_SIZE) {
    return 'File exceeds maximum allowed size.';
  }
  if (audio.statusReason === AudioStatusReasonType.RETRYABLE) {
    return 'Upload failure.';
  }
  return 'Unexpected failure occurred.';
};

export const zeroPad = (num: number, places: number) =>
  String(num).padStart(places, '0');

export const getFormattedDuration = (ms?: number) => {
  if (!ms) return null;
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms / 60000) % 60);
  const seconds = Math.floor((ms / 1000) % 60);
  return `${hours === 0 ? '' : `${zeroPad(hours, 2)}:`}${zeroPad(
    minutes,
    2
  )}:${zeroPad(seconds, 2)}`;
};

export const getBytestoMBText = (sizeInBytes?: number) =>
  sizeInBytes ? `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB` : null;
